import React from "react";
import { fromJS } from "immutable";
import { Box, Input, Text, Select, SelectOption, Switch } from "@showitinc/showit-wc";

import AppConstants, { TEXT_STYLE_IDS } from "../../constants/AppConstants";
import HelpTooltip from "../HelpTooltip";
import FontSelectInput from "../FontSelectInput";
import AlignOptions from "../elementproperties/AlignOptions";
import { INPUT_TYPE_NUMBER } from "../../constants/ElementConstants";
import PropertyMaps from "../../constants/PropertyMaps";
import useStyleSync from "../../hooks/useStyleSync";
import {
  TEXT_ALIGNMENT,
  TEXT_COLOR,
  TEXT_FONT,
  TEXT_FORMAT,
  TEXT_HOVER_COLOR,
  TEXT_HOVER_FORMAT,
  TEXT_LETTER_CASE,
  TEXT_LETTER_SPACING,
  TEXT_LINE_HEIGHT,
  TEXT_LINK,
  TEXT_LINK_COLOR,
  TEXT_LINK_FORMAT,
  TEXT_LINK_HOVER_COLOR,
  TEXT_LINK_HOVER_FORMAT,
  TEXT_MARGIN_BOTTOM,
  TEXT_SIZE,
} from "../../constants/MergeConstants";
import SplitMergeWrapper from "../../app2components/base/SplitMergeWrapper";
import ColorPicker from "../../app2components/base/ColorPicker";
import { FLYOUT_SHIFT_RIGHT } from "../../constants/UIConstants";
import PropertyPanelSectionWrapper from "../../app2components/base/PropertyPanelSectionWrapper";
import dsStyles from "./DesignSettings.module.css";

const { STYLE_PROPS_THEME_TEXT, TEXT_NESTED_INLINE_LINK } = PropertyMaps;
const { THEME_STYLES } = AppConstants;

export const propGetter = (prop, props, fallbackProps) =>
  fallbackProps ? props.get(prop) ?? fallbackProps.get(prop) : props.get(prop);

export const prePrepChange = (props, fallbackProps) => (prop, getterPropName) =>
  [prop, propGetter(getterPropName ?? prop, props, fallbackProps)];

/**
 * @typedef TypeStyleProps
 * @prop {string} type - type identifier
 * @prop {Map} typeStyles - type style properties
 * @prop {string} selectedLayout - selected layout (mobile or desktop)
 * @prop {Function} onChange - prop update handler
 * @prop {string} selectedType - currently selected text type
 * @prop {Function} setSelectedType - text type selection handler
 * @prop {boolean} showInlineLinkPreview - show inline link preview state
 * @prop {Function} setShowInlineLinkPreview - inline link preview toggle handler
 * @prop {Map} palette - Color palette
 */
const TypeStyle = ({
  type,
  typeStyles,
  selectedLayout,
  onChange,
  selectedType,
  setSelectedType,
  showInlineLinkPreview,
  setShowInlineLinkPreview,
  palette,
}) => {
  const {
    mergedSyncProps,
    mergedLinkSyncProps,
    onFormatChange,
    addToggle,
    prepChange,
    prepLinkChange,
  } = useStyleSync({
    parentType: THEME_STYLES,
    childType: type,
    styles: typeStyles,
    selectedLayout,
    onChange,
    propKeys: STYLE_PROPS_THEME_TEXT,
    linkConfig: {
      linkStyleProps: TEXT_NESTED_INLINE_LINK,
      textColorProp: TEXT_COLOR,
      hoverColorProp: TEXT_HOVER_COLOR,
      linkProp: TEXT_LINK,
    },
  });

  return (
    <PropertyPanelSectionWrapper
      key={`style-${type}`}
      title={typeStyles.get("name")}
      open={type === selectedType}
      onClick={() => setSelectedType(type)}
    >
      <div className="panel-section-content">
        <SplitMergeWrapper {...addToggle(TEXT_COLOR)}>
          <ColorPicker
            color={mergedSyncProps.get(TEXT_COLOR)}
            context
            shift={FLYOUT_SHIFT_RIGHT}
            themeOnly
            customPalette={palette}
            onSIBeforeChange={onFormatChange(...prepChange(TEXT_COLOR))}
            onSIChange={onFormatChange(...prepChange(TEXT_COLOR))}
            onSIInput={onFormatChange(...prepChange(TEXT_COLOR), false)}
          />
        </SplitMergeWrapper>
        <SplitMergeWrapper {...addToggle(TEXT_FONT)}>
          <FontSelectInput
            label="Font"
            value={mergedLinkSyncProps.get(TEXT_FONT, mergedSyncProps.get(TEXT_FONT))}
            onChange={onFormatChange(TEXT_FONT, mergedSyncProps.get(TEXT_FONT))}
            context
          />
        </SplitMergeWrapper>
        <SplitMergeWrapper {...addToggle(TEXT_SIZE)}>
          <Input
            type={INPUT_TYPE_NUMBER}
            min="1"
            step="1"
            value={mergedLinkSyncProps.get(TEXT_SIZE, mergedSyncProps.get(TEXT_SIZE))}
            label="Size"
            units="px"
            onSIBeforeChange={onFormatChange(...prepChange(TEXT_SIZE))}
            onSIInput={onFormatChange(...prepChange(TEXT_SIZE), false)}
            onSIChange={onFormatChange(...prepChange(TEXT_SIZE))}
            context
          />
        </SplitMergeWrapper>
        <SplitMergeWrapper {...addToggle(TEXT_LINE_HEIGHT)}>
          <Input
            type={INPUT_TYPE_NUMBER}
            min="0.1"
            precision="3"
            step="0.1"
            value={mergedLinkSyncProps.get(TEXT_LINE_HEIGHT, mergedSyncProps.get(TEXT_LINE_HEIGHT))}
            label="Line Height"
            units="u"
            onSIBeforeChange={onFormatChange(...prepChange(TEXT_LINE_HEIGHT))}
            onSIInput={onFormatChange(...prepChange(TEXT_LINE_HEIGHT), false)}
            onSIChange={onFormatChange(...prepChange(TEXT_LINE_HEIGHT))}
            context
          />
        </SplitMergeWrapper>
        <SplitMergeWrapper {...addToggle(TEXT_LETTER_SPACING)}>
          <Input
            type={INPUT_TYPE_NUMBER}
            step="0.1"
            precision="3"
            value={mergedSyncProps.get(TEXT_LETTER_SPACING)}
            label="Letter Spacing"
            units="em"
            onSIBeforeChange={onFormatChange(...prepChange(TEXT_LETTER_SPACING))}
            onSIInput={onFormatChange(...prepChange(TEXT_LETTER_SPACING), false)}
            onSIChange={onFormatChange(...prepChange(TEXT_LETTER_SPACING))}
            context
          />
        </SplitMergeWrapper>
        <SplitMergeWrapper {...addToggle(TEXT_LETTER_CASE)}>
          <Select
            label="Letter Case"
            reserveContext
            value={mergedSyncProps.get(TEXT_LETTER_CASE, "")}
            onSISelect={onFormatChange(TEXT_LETTER_CASE, mergedSyncProps.get(TEXT_LETTER_CASE, ""))}
            context
          >
            <SelectOption value="">Normal</SelectOption>
            <SelectOption value="uppercase">UPPERCASE</SelectOption>
            <SelectOption value="lowercase">lowercase</SelectOption>
            <SelectOption value="capitalize">Title Case</SelectOption>
          </Select>
        </SplitMergeWrapper>
        <SplitMergeWrapper {...addToggle(TEXT_MARGIN_BOTTOM)}>
          <Input
            type={INPUT_TYPE_NUMBER}
            min="0"
            step="1"
            value={mergedSyncProps.get(TEXT_MARGIN_BOTTOM)}
            label="Bottom Margin"
            units="px"
            onSIBeforeChange={onFormatChange(...prepChange(TEXT_MARGIN_BOTTOM))}
            onSIInput={onFormatChange(...prepChange(TEXT_MARGIN_BOTTOM), false)}
            onSIChange={onFormatChange(...prepChange(TEXT_MARGIN_BOTTOM))}
            context
          />
        </SplitMergeWrapper>
        <SplitMergeWrapper {...addToggle(TEXT_ALIGNMENT)}>
          <AlignOptions
            label="Text Align"
            propName={mergedSyncProps.get(TEXT_ALIGNMENT)}
            alignment={mergedSyncProps.get(TEXT_ALIGNMENT) || "left"}
            onSelect={onFormatChange(TEXT_ALIGNMENT, mergedSyncProps.get(TEXT_ALIGNMENT, "left"))}
            sync={mergedSyncProps.get(TEXT_ALIGNMENT) !== undefined}
            toggleSync={() => addToggle(TEXT_ALIGNMENT).toggleSync()}
            context
          />
        </SplitMergeWrapper>

        {/* INLINE LINKS */}
        <Text className="panel-subheading">Inline Links</Text>
        <Switch
          label="Show link styles"
          checked={showInlineLinkPreview}
          onSIChange={() => setShowInlineLinkPreview(!showInlineLinkPreview)}
        />
        <SplitMergeWrapper {...addToggle(TEXT_LINK_COLOR, mergedLinkSyncProps.get(TEXT_COLOR))}>
          <ColorPicker
            color={mergedLinkSyncProps.get(TEXT_COLOR)}
            context
            shift={FLYOUT_SHIFT_RIGHT}
            customPalette={palette}
            onSIBeforeChange={onFormatChange(...prepLinkChange(TEXT_LINK_COLOR, TEXT_COLOR))}
            onSIChange={onFormatChange(...prepLinkChange(TEXT_LINK_COLOR, TEXT_COLOR))}
            onSIInput={onFormatChange(...prepLinkChange(TEXT_LINK_COLOR, TEXT_COLOR), false)}
          />
        </SplitMergeWrapper>

        <SplitMergeWrapper {...addToggle(TEXT_LINK_FORMAT, mergedLinkSyncProps.get(TEXT_FORMAT))}>
          <Select
            label="Format"
            reserveContext
            value={mergedLinkSyncProps.get(TEXT_FORMAT, "")}
            onSISelect={onFormatChange(...prepLinkChange(TEXT_LINK_FORMAT, TEXT_FORMAT))}
            context
          >
            <SelectOption value="">Underline</SelectOption>
            <SelectOption value="none">No Underline</SelectOption>
          </Select>
        </SplitMergeWrapper>
        <SplitMergeWrapper {...addToggle(TEXT_LINK_HOVER_COLOR)}>
          <ColorPicker
            color={mergedLinkSyncProps.get(TEXT_HOVER_COLOR)}
            context
            label="Hover Color"
            shift={FLYOUT_SHIFT_RIGHT}
            customPalette={palette}
            onSIBeforeChange={onFormatChange(
              ...prepLinkChange(TEXT_LINK_HOVER_COLOR, TEXT_HOVER_COLOR)
            )}
            onSIChange={onFormatChange(...prepLinkChange(TEXT_LINK_HOVER_COLOR, TEXT_HOVER_COLOR))}
            onSIInput={onFormatChange(
              ...prepLinkChange(TEXT_LINK_HOVER_COLOR, TEXT_HOVER_COLOR),
              false
            )}
          />
        </SplitMergeWrapper>

        <SplitMergeWrapper {...addToggle(TEXT_LINK_HOVER_FORMAT)}>
          <Select
            label="Hover Format"
            reserveContext
            value={mergedLinkSyncProps.get(TEXT_HOVER_FORMAT)}
            onSISelect={onFormatChange(
              ...prepLinkChange(TEXT_LINK_HOVER_FORMAT, TEXT_HOVER_FORMAT)
            )}
            context
          >
            <SelectOption value="">Underline</SelectOption>
            <SelectOption value="none">No Underline</SelectOption>
          </Select>
        </SplitMergeWrapper>
      </div>
    </PropertyPanelSectionWrapper>
  );
};

/**
 * Type styles configuration panel component
 * @param {DesignSettingsTypeStylesProps} props
 */
const DesignSettingsTypeStyles = ({
  onChange,
  children,
  newTheme,
  selectedLayout,
  selectedType,
  setSelectedType,
  showInlineLinkPreview,
  setShowInlineLinkPreview,
  palette,
}) => {
  const styles = newTheme.get(THEME_STYLES, fromJS({}));

  return (
    <>
      {TEXT_STYLE_IDS.map((type) => (
        <TypeStyle
          key={type}
          type={type}
          typeStyles={styles.get(type)}
          selectedLayout={selectedLayout}
          onChange={onChange}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          showInlineLinkPreview={showInlineLinkPreview}
          setShowInlineLinkPreview={setShowInlineLinkPreview}
          palette={palette}
        />
      ))}
      {children}
    </>
  );
};

export default DesignSettingsTypeStyles;
